import { computed, onBeforeUnmount, unref } from 'vue'
import { onBeforeRouteLeave } from 'vue-router'
import type { MaybeRef } from 'vue'
import { useRoute } from 'vue-router'
import { ALLOW_ORDER_SUCCESS } from '@/constants/order'
import { isNativeShellApp } from '@/utils/appBridgeGate'

const ALERT_MSG = '만료된 페이지예요. 홈으로 이동할게요.'
const REDIRECT_PATH_HOME = '/'
const SUCCESS_EXITED_KEY_PREFIX = 'success-page-exited'

export const useBFCacheGuard = (successPageId?: MaybeRef<string>) => {
  const route = useRoute()

  const partnerKey = computed(() => route.params.partner || route.query.partnerKey)

  const redirectPath = computed(() =>
    partnerKey.value ? `/partners/${partnerKey.value}${REDIRECT_PATH_HOME}` : REDIRECT_PATH_HOME
  )

  const successExitedKey = computed(() => {
    const id = successPageId ? unref(successPageId) : ''

    return id ? `${SUCCESS_EXITED_KEY_PREFIX}:${id}` : SUCCESS_EXITED_KEY_PREFIX
  })

  let removeSuccessPageShowListener: (() => void) | null = null

  /**
   * "만료된 페이지 → 홈" 처리의 **단일 진입점**.
   *
   * 이 파일에는 같은 처리가 네 군데 있었는데 앱 분기가 한 곳에만 들어가 있어,
   * 결제 페이지로 되돌아오는 경로(`guardBFCacheOrderPage`)에서 앱인데도 웹 홈으로
   * 이동하는 버그가 있었다. 중복이 원인이었으므로 전부 이 함수를 거치게 한다.
   *
   * @param delayMs BFCache `pageshow` 복귀처럼 alert 이 닫힌 뒤에 이동해야 하는
   *   경우의 지연(ms). 앱 분기는 alert 이 없어 지연도 필요 없다.
   */
  const redirectToExpiredPage = (message: string, path: string, delayMs = 0) => {
    if (isNativeShellApp()) {
      // 2.2.7 이상: 만료 안내 없이 네이티브 홈으로 복귀
      window.flutter_inappwebview?.callHandler('openNativeScreen', {
        screen: 'home'
      })
      return
    }

    // 2.2.6 이하 / 일반 브라우저: 안내 후 이동
    alert(message)

    if (delayMs > 0) {
      setTimeout(() => {
        window.location.replace(path)
      }, delayMs)
      return
    }

    window.location.replace(path)
  }

  /**
   * 결제 페이지로 BFCache 복귀하는 것을 방지
   */
  const guardBFCacheOrderPage = (msg?: string, customRedirectPath?: string) => {
    const alertMessage = msg || ALERT_MSG
    const path = customRedirectPath || redirectPath.value

    const allow = sessionStorage.getItem(ALLOW_ORDER_SUCCESS)

    const navigation = performance.getEntriesByType('navigation')[0] as
      | PerformanceNavigationTiming
      | undefined

    const isBackForward = navigation?.type === 'back_forward' || performance.navigation?.type === 2

    if (allow && isBackForward) {
      sessionStorage.removeItem(ALLOW_ORDER_SUCCESS)
      redirectToExpiredPage(alertMessage, path)
      return true
    }

    const handlePageShow = (event: PageTransitionEvent) => {
      const currentAllow = sessionStorage.getItem(ALLOW_ORDER_SUCCESS)

      if (currentAllow && event.persisted) {
        sessionStorage.removeItem(ALLOW_ORDER_SUCCESS)
        redirectToExpiredPage(alertMessage, path, 100)
      }
    }

    window.addEventListener('pageshow', handlePageShow)

    sessionStorage.removeItem(ALLOW_ORDER_SUCCESS)

    return false
  }

  /**
   * 성공 페이지 재진입 방지
   *
   * 반환값:
   * true  - 재진입이므로 리다이렉트 실행
   * false - 최초 진입 또는 새로고침
   */
  const guardBFCacheSuccessPage = (msg?: string, customRedirectPath?: string) => {
    const alertMessage = msg || ALERT_MSG
    const path = customRedirectPath || redirectPath.value
    const exitedKey = successExitedKey.value

    /**
     * SPA 라우팅으로 성공 페이지를 빠져나갔다가
     * router.back() 등으로 다시 돌아온 경우
     */
    const hasExitedSuccessPage = sessionStorage.getItem(exitedKey) === 'true'

    if (hasExitedSuccessPage) {
      redirectToExpiredPage(alertMessage, path)
      return true
    }

    const navigation = performance.getEntriesByType('navigation')[0] as
      | PerformanceNavigationTiming
      | undefined

    /**
     * BFCache가 아닌 일반 브라우저 뒤로가기로
     * 문서가 다시 로드된 경우
     */
    const isBackForward = navigation?.type === 'back_forward' || performance.navigation?.type === 2

    if (isBackForward) {
      redirectToExpiredPage(alertMessage, path)
      return true
    }

    /**
     * Safari BFCache 복귀 대응
     */
    const handlePageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) {
        return
      }

      redirectToExpiredPage(alertMessage, path, 100)
    }

    window.addEventListener('pageshow', handlePageShow)

    removeSuccessPageShowListener = () => {
      window.removeEventListener('pageshow', handlePageShow)
    }

    return false
  }

  /**
   * Vue Router를 통해 다른 페이지로 이동한 경우에만 기록됩니다.
   *
   * 단순 새로고침에서는 onBeforeRouteLeave가 호출되지 않으므로
   * 성공 페이지가 계속 노출됩니다.
   */
  onBeforeRouteLeave(() => {
    if (!successPageId || !unref(successPageId)) {
      return
    }

    sessionStorage.setItem(successExitedKey.value, 'true')
  })

  onBeforeUnmount(() => {
    removeSuccessPageShowListener?.()
  })

  const guardBFCachePaymentsPage = (msg?: string, customRedirectPath?: string) => {
    const isPaymentSuccess = sessionStorage.getItem(ALLOW_ORDER_SUCCESS) === 'true'

    if (!isPaymentSuccess) {
      return false
    }

    const alertMessage = msg || ALERT_MSG
    const path = customRedirectPath || redirectPath.value

    const navigation = performance.getEntriesByType('navigation')[0] as
      | PerformanceNavigationTiming
      | undefined

    const isBackForward = navigation?.type === 'back_forward' || performance.navigation?.type === 2

    if (isBackForward) {
      sessionStorage.removeItem(ALLOW_ORDER_SUCCESS)
      redirectToExpiredPage(alertMessage, path)
      return true
    }

    const handlePageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) {
        return
      }

      const isPaymentSuccess = sessionStorage.getItem(ALLOW_ORDER_SUCCESS) === 'true'

      if (!isPaymentSuccess) {
        return
      }

      sessionStorage.removeItem(ALLOW_ORDER_SUCCESS)
      redirectToExpiredPage(alertMessage, path, 100)
    }

    window.addEventListener('pageshow', handlePageShow, {
      once: true
    })

    return false
  }

  return {
    guardBFCacheOrderPage,
    guardBFCacheSuccessPage,
    partnerKey
  }
}
